// AdapterView.jsx — Grid + filter tabs + search for the Adapters side panel tab.

import { useState, useMemo } from 'react';
import { Search, Loader2, Layers } from 'lucide-react';
import { useLLMAdapters } from '../../../hooks/useLLMAdapters.js';
import { ADAPTER_KINDS, KIND_META } from '../../../lib/adapterCatalog.js';
import AdapterCard from './AdapterCard.jsx';
import AdapterAddTile from './AdapterAddTile.jsx';
import AdapterAddModal from './AdapterAddModal.jsx';
import AdapterSheet from './AdapterSheet.jsx';
import { partitionByStatus } from './partitionByStatus.js';

export default function AdapterView() {
  const { adapters, loading, error, create, update, remove, test, listModels } = useLLMAdapters();
  const [activeKind, setActiveKind] = useState('all');
  const [query, setQuery] = useState('');
  const [openSheet, setOpenSheet] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const filtered = useMemo(() => {
    const byKind =
      activeKind === 'all' ? adapters : adapters.filter((a) => a.kind === activeKind);
    if (!query.trim()) return byKind;
    const q = query.trim().toLowerCase();
    return byKind.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.display_name.toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q),
    );
  }, [adapters, activeKind, query]);

  const kindCounts = useMemo(() => {
    const counts = { all: adapters.length };
    for (const k of ADAPTER_KINDS) counts[k] = adapters.filter((a) => a.kind === k).length;
    return counts;
  }, [adapters]);

  // Status-first partition (Cursor parity). Runs WITHIN the search + kind
  // filter so both sections honor the active filter. "attention" = NOT
  // connected (no key / inactive) and renders FIRST; "connected" = key
  // present / active (NOT a reachability claim).
  const { attention, connected } = useMemo(
    () => partitionByStatus(filtered),
    [filtered],
  );

  return (
    <div className="flex h-full flex-col text-apolo-textPrimary">
      <header className="border-b border-apolo-borderSubtle p-3">
        <div className="mb-2 flex items-center gap-2">
          <Layers size={14} aria-hidden="true" />
          <h2 className="text-sm font-semibold">Adapters</h2>
          <span className="ml-auto text-xs text-apolo-textTertiary">
            {adapters.length} disponíveis
          </span>
        </div>
        <div className="mb-2 flex items-center gap-1 rounded-md bg-apolo-surface2 px-2 py-1">
          <Search size={14} aria-hidden="true" className="text-apolo-textTertiary" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar adapter…"
            aria-label="Buscar adapter"
            className="flex-1 bg-transparent text-sm placeholder:text-apolo-textTertiary focus:outline-none"
          />
        </div>
        <nav className="flex flex-wrap gap-1" aria-label="Filtrar por tipo">
          <FilterTab
            label="All"
            count={kindCounts.all}
            active={activeKind === 'all'}
            onClick={() => setActiveKind('all')}
          />
          {ADAPTER_KINDS.map((k) => (
            <FilterTab
              key={k}
              label={KIND_META[k].label}
              icon={KIND_META[k].icon}
              count={kindCounts[k]}
              active={activeKind === k}
              onClick={() => setActiveKind(k)}
            />
          ))}
        </nav>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="flex h-full items-center justify-center text-apolo-textTertiary">
            <Loader2 size={18} className="mr-2 animate-spin" aria-hidden="true" />
            Carregando adapters…
          </div>
        ) : error ? (
          <div
            role="alert"
            className="rounded-md border border-apolo-error/40 bg-apolo-error/10 px-3 py-2 text-sm text-apolo-error"
          >
            {error}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            <AdapterAddTile onClick={() => setAddModalOpen(true)} />
            <StatusSection
              label="Atenção"
              hint="Sem chave / inativo"
              tone="amber"
              adapters={attention}
              onOpen={setOpenSheet}
            />
            <StatusSection
              label="Conectados"
              hint="Conectado"
              tone="green"
              adapters={connected}
              onOpen={setOpenSheet}
            />
            {filtered.length === 0 && (
              <div className="col-span-2 mt-4 text-center text-sm text-apolo-textTertiary">
                Nenhum adapter encontrado. Tente outro filtro ou clique em + Adicionar adapter.
              </div>
            )}
          </div>
        )}
      </div>

      {addModalOpen && (
        <AdapterAddModal
          onClose={() => setAddModalOpen(false)}
          onCreate={async (input) => {
            const res = await create(input);
            if (res.ok) {
              setAddModalOpen(false);
              setOpenSheet(res.data?.adapter || null);
            }
            return res;
          }}
        />
      )}

      {openSheet && (
        <AdapterSheet
          adapter={openSheet}
          onClose={() => setOpenSheet(null)}
          onSave={async (patch) => {
            const res = await update(openSheet.id, patch);
            if (res.ok && res.data?.adapter) setOpenSheet(res.data.adapter);
            return res;
          }}
          onDelete={async () => {
            const res = await remove(openSheet.id);
            if (res.ok) setOpenSheet(null);
            return res;
          }}
          onTest={async () => test(openSheet.id)}
          onListModels={async () => listModels(openSheet.id)}
        />
      )}
    </div>
  );
}

// Status-grouped section (Cursor parity). Renders nothing when empty so a
// filtered-out bucket leaves no orphan header. `hint` keeps the label honest:
// status = key-presence/active, NOT a reachability/"answered" claim.
function StatusSection({ label, hint, tone, adapters, onOpen }) {
  if (!adapters || adapters.length === 0) return null;
  const dot = tone === 'green' ? 'bg-emerald-400' : 'bg-amber-400';
  return (
    <section aria-label={label}>
      <div className="mb-1.5 mt-1 flex items-center gap-1.5">
        <span aria-hidden="true" className={`h-2 w-2 rounded-full ${dot}`} />
        <h3 className="text-xs font-semibold text-apolo-textSecondary">{label}</h3>
        <span className="rounded-full bg-apolo-surface2 px-1.5 text-[10px] tabular-nums text-apolo-textTertiary">
          {adapters.length}
        </span>
        <span className="ml-auto text-[10px] text-apolo-textTertiary">{hint}</span>
      </div>
      <div className="grid grid-cols-1 gap-2">
        {adapters.map((adapter) => (
          <AdapterCard key={adapter.id} adapter={adapter} onClick={() => onOpen(adapter)} />
        ))}
      </div>
    </section>
  );
}

function FilterTab({ label, icon, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        'inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[11px] transition ' +
        (active
          ? 'bg-apolo-accent text-apolo-bg'
          : 'bg-apolo-surface2 text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary')
      }
    >
      {icon && <span aria-hidden="true" className="text-[10px]">{icon}</span>}
      <span>{label}</span>
      <span className="rounded-full bg-black/20 px-1 text-[9px] tabular-nums">
        {count}
      </span>
    </button>
  );
}
